import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../UI/Button';
import { useTranslation } from 'react-i18next';
import { getLanguagePathPrefix } from '../../i18n';

export const NightclubCTA: React.FC = () => {
  const { t } = useTranslation(['common', 'home']);
  
  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background Video - Nightclub vibe */}
      <video
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover z-0"
      >
        <source src="/images/blog/nightclub2.mp4" type="video/mp4" />
      </video>
      {/* Overlay léger pour lisibilité du texte */}
      <div className="absolute inset-0 z-[1] bg-black/40" />
      
      <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 drop-shadow-[0_4px_12px_rgba(0,0,0,1)] [text-shadow:_2px_2px_8px_rgb(0_0_0_/_90%)]">
            {(() => {
              const title = t('home:nightclub.cta.title');
              const parts = title.split(' ');
              // Dernier mot en violet (peu importe la langue)
              if (parts.length > 1) {
                return (
                  <>
                    {parts.slice(0, -1).join(' ')} <span className="text-purple-400">{parts[parts.length - 1]}</span>
                  </>
                );
              }
              return <span className="text-purple-400">{title}</span>;
            })()}
          </h2>
          <p className="text-xl md:text-2xl font-semibold text-yellow-400 mb-4 italic drop-shadow-[0_3px_8px_rgba(0,0,0,1)] [text-shadow:_1px_1px_6px_rgb(0_0_0_/_80%)]">
            {t('home:nightclub.cta.slogan')}
          </p>
          <p className="text-xl text-white/90 mb-8 leading-relaxed drop-shadow-[0_3px_8px_rgba(0,0,0,1)] [text-shadow:_1px_1px_6px_rgb(0_0_0_/_80%)]">
            {t('home:nightclub.cta.subtitle')}
          </p>
          
          {/* Features list */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <div className="flex items-center justify-center space-x-3">
              <CheckCircle className="h-6 w-6 text-purple-400 flex-shrink-0 drop-shadow-[0_3px_6px_rgba(0,0,0,1)]" />
              <span className="text-white/90 drop-shadow-[0_3px_6px_rgba(0,0,0,1)] [text-shadow:_1px_1px_4px_rgb(0_0_0_/_90%)]">{t('home:nightclub.cta.features.freeTrial')}</span>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <CheckCircle className="h-6 w-6 text-purple-400 flex-shrink-0 drop-shadow-[0_3px_6px_rgba(0,0,0,1)]" />
              <span className="text-white/90 drop-shadow-[0_3px_6px_rgba(0,0,0,1)] [text-shadow:_1px_1px_4px_rgb(0_0_0_/_90%)]">{t('home:nightclub.cta.features.support')}</span>
            </div>
          </div>
          
          {/* DJ Mag Top 100 Special Offer */}
          <div className="mb-10 p-6 bg-purple-900/40 backdrop-blur-sm rounded-2xl border-2 border-purple-500/50 shadow-[0_6px_20px_rgba(0,0,0,1)]">
            <p className="text-yellow-400 font-bold text-lg mb-2 drop-shadow-[0_3px_8px_rgba(0,0,0,1)] [text-shadow:_2px_2px_6px_rgb(0_0_0_/_90%)]">
              {t('home:nightclub.cta.djMagOffer.title')}
            </p>
            <p className="text-white/90 drop-shadow-[0_3px_6px_rgba(0,0,0,1)] [text-shadow:_1px_1px_4px_rgb(0_0_0_/_90%)]">
              {t('home:nightclub.cta.djMagOffer.description')}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="glossy" size="lg" className="text-lg px-8 py-4 rounded-full bg-purple-600 hover:bg-purple-700 shadow-[0_6px_20px_rgba(0,0,0,1)]" onClick={() => { 
              window.location.href = `${getLanguagePathPrefix()}/nightclub/pricing/`;
            }}>
              <span>{t('home:nightclub.cta.button.primary')}</span>
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="text-white border-white hover:bg-white/10 text-lg px-8 py-4 rounded-full shadow-[0_6px_20px_rgba(0,0,0,1)]"
              onClick={() => { 
                window.location.href = `${getLanguagePathPrefix()}/contact`;
              }}
            >
              {t('common:cta.viewDemo')}
            </Button>
          </div>
          
          <p className="text-white/60 mt-6 text-sm drop-shadow-[0_3px_6px_rgba(0,0,0,1)] [text-shadow:_1px_1px_4px_rgb(0_0_0_/_90%)]">
            {t('home:nightclub.cta.footer')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

